import { Button, CardContent, Dialog, DialogContent, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import locationsService from 'services/locations.service';
import { isLocationRequiredPopupState, isWaitingForPollState, manualRefreshState } from '../RecoilState';
import { LocationEdit } from './LocationEdit';

import useCustomModal from 'components/CustomConfirm';
import Lottie from 'react-lottie-player';
import animationData from "src/pages/ActionLayout/Finalize/anim_completion.json";
import { InvalidLocationConfirm } from './InvalidLocationConfirm';

import { openChatState, SUPPORT_CHAT } from 'pages/CustomerBooking';
import { useTrackEvent, useTrackPage } from 'util/trackEvent';
import { requiresLocationConfirmation, requiresNewLocation } from 'util/bookingStates';
import { fetchBooking } from 'services/booking.service';
import { useStateRef } from '@zennya/web-component-library/src';
import { useHistory, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      alignSelf: 'center',
      textAlign: 'center',
      paddingBottom: theme.spacing(4),
      marginTop: theme.spacing(-4),
    },
    lottie: {
      height: 200
    },
    contentTitle: {
      lineHeight: '2em',
      paddingBottom: theme.spacing(2)
    },
    contentSubtext: {
      lineHeight: '2em',
      paddingBottom: theme.spacing(3)
    },
    loc_scrollPaper: {
      [theme.breakpoints.down("xs")]: {
        height: "75vh",
        overflowY: "scroll",

      },
    },
    loc_container: {
      [theme.breakpoints.down("xs")]: {

      },
    },
    loc_dialog: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        display: "flex",
        justifyContent: "flex-end",
      },
    },
    loc_paper: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
    }


  }
})
const LocationConfirmDialog = ({
  forceRequireNewLocation: _forceRequireNewLocation = false,
  asDialog = true,
  onLocationUpdated = (b) => { },
  confirmContext, isUpdate = false, booking: initBooking }) => {
  const setChatOpen = useSetRecoilState(openChatState);
  const confirm = useCustomModal()
  const classes = useStyles()
  const isOpen = useRecoilValue(isLocationRequiredPopupState)
  const setIsOpen = useSetRecoilState(isLocationRequiredPopupState);

  const history = useHistory();
  const browserLoc = useLocation()
  const setManualRefreshState = useSetRecoilState(manualRefreshState);
  const [isWaitingForPoll, setIsWaitingForPoll] = useRecoilState(isWaitingForPollState);

  const [editing, setEditing] = useState(false);

  const [dirty, setDirty, dirtyRef] = useStateRef(false);

  const [locationUpdated, setLocationUpdated] = useState(false);

  const bookingRef = useRef()

  const [updatedAddress, setUpdatedAddress] = useState();

  const [forceRequireNewLocation, setForceRequireNewLocation] = useState(_forceRequireNewLocation);

  const [loading, setLoading] = useState(false);
  const [updateLocationError, setUpdateLocationError] = useState();

  const [existingLocations, setExistingLocations] = useState([]);

  const fetchExistingLocations = useCallback(
    async () => {
      const locResult = await locationsService.getUserLocations(bookingRef?.current?.client?.id, { confirmedOnly: true })
      setExistingLocations(locResult)
    },
    [bookingRef],
  );


  useEffect(() => {
    if (!bookingRef?.current && !isEmpty(initBooking)) {
      bookingRef.current = initBooking
      setUpdatedAddress(bookingRef?.current?.user_location)
      fetchExistingLocations()
    }
  }, [initBooking]);

  const trackEvent = useTrackEvent()
  const trackPage = useTrackPage()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  useEffect(
    () => {
      if (isOpen) {
        trackPage('Location Confirmation')
      }
    }, [
    isOpen
  ]
  );

  const updateAndOrConfirm = useCallback(
    async (props) => {
      const locationId = props?.locationId
      try {
        setIsWaitingForPoll(true)
        setLoading(true);
        let location = null;
        if (dirtyRef.current) {
          if (locationId) {
            await locationsService.setWhitelabelUserLocationById(bookingRef?.current?.id, locationId)
          } else {
            await locationsService.updateWhitelabelBookingLocation(bookingRef?.current?.id, updatedAddress)
          }
        } else {
          if (locationId) {
            await locationsService.setWhitelabelUserLocationById(bookingRef?.current?.id, locationId)
          }
        }
        const res = await locationsService.confirmWhitelabelUserLocation(bookingRef?.current?.id)
        setManualRefreshState(Math.random())
        setLoading(false);

        asDialog &&
          setLocationUpdated(true)

        onLocationUpdated(location)
        history.replace({
          pathname: `/booking/${bookingRef?.current?.id}`,
          search: browserLoc?.search
        })

      } catch (error) {
        setIsWaitingForPoll(false)
        confirm(props => {
        
          return (
            <InvalidLocationConfirm
              {...props}
              updatedLocation={updatedAddress}
              locationId={locationId}
              trackEvent={trackEvent}
              confirm={confirm}
              error={error?.response?.data}
              onOpenSupport={() => {
                setChatOpen(SUPPORT_CHAT)
                setIsOpen(false)
              }}
              onClose={() => {
                props.onClose()
                setIsOpen(false)
              }}
              onSelectEdit={() => {
                setEditing(true)
                props.onClose()
              }}
              booking={bookingRef?.current} />
          )
        })

        setLoading(false);
      }
    },
    [dirtyRef.current, updatedAddress, bookingRef],
  );

  const content = useMemo(
    () => {
      let dialogTitle = ''
      if (dirtyRef.current) {
        dialogTitle = 'Move the pin to your exact location'
      } else {
        if (isUpdate) {
          dialogTitle = 'Update your location'
        } else {
          if (requiresNewLocation(bookingRef?.current)) {
            dialogTitle = 'Step 1: Set your location'
          } else {
            dialogTitle = 'Step 1: Confirm your location'
          }
        }
      }
      return (locationUpdated && !_forceRequireNewLocation) ? (
        <>
          <Lottie
            className={classes.lottie}
            play
            loop={0}
            animationData={animationData}
          />
          <CardContent className={classes.root}>
            <Typography variant='h4' className={classes.contentTitle}>
              Step 1 Complete.
            </Typography>
            <Typography variant='body2' className={classes.contentSubtext}>
              Your location is updated.<br />Please confirm your booking on the following screen.
            </Typography>
            <Button variant='contained' size="large" color='primary' onClick={() => {
              trackEvent('Your location is updated - OK', { dialog: 'LocationConfirmDialog' })
              setIsOpen(false)
              setEditing(false)
              setLocationUpdated(false)
            }}>Ok</Button>
          </CardContent>
        </>
      ) :
        (

          <LocationEdit
            existingLocations={existingLocations}
            dialogTitle={dialogTitle}
            loading={loading}
            isEditing={editing}
            location={updatedAddress || {
              latitude: bookingRef?.current?.regional_office?.latitude,
              longitude: bookingRef?.current?.regional_office?.longitude,
              newLocation: true
            }}
            requiresNewLocation={requiresNewLocation(bookingRef?.current)}
            forceRequireNewLocation={forceRequireNewLocation}
            onSelectConfirm={(v) => {
              updateAndOrConfirm()

            }}
            onSelectExistingConfirm={(v) => {
              setDirty(true)
              updateAndOrConfirm({locationId:v})
            }}
            onSelectEdit={(v) => {
              setEditing(v)
            }}
            onUpdateAddress={(v) => {
              const { newLocation, ...rest } = v
              setUpdatedAddress(rest)
              setDirty(true)
              setForceRequireNewLocation(false)
            }}

          />

        )
    }, [updatedAddress, existingLocations, locationUpdated, bookingRef, dirtyRef.current, isUpdate, editing, loading, updateAndOrConfirm, classes, setIsOpen, setEditing, setLocationUpdated]
  );

  return asDialog ? (
    <Dialog
      // fullScreen={isMobile}
      keepMounted
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      open={isOpen}
      className={classes.loc_dialog}
      classes={editing ? {} : {
        paper: classes.loc_paper,
        scrollPaper: classes.loc_scrollPaper,
        container: classes.loc_container
      }}
    // onClose={() => {
    //   setIsOpen(false)
    //   setEditing(false)
    //   setLocationUpdated(false)
    // }}
    >
      {content}


    </Dialog>
  ) : content
}

export { LocationConfirmDialog };
